import { Box, Card, Typography, Divider, Chip, IconButton, Button, Stack, TextField } from "@mui/material";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../../router/constants";
import EditIcon from "@mui/icons-material/Edit";
import type { IUser } from "../../interfaces";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { searchDoctors, type DoctorSearchResult } from "../../services/doctors.service";
import { linkDoctorsToSecretary } from "../../services/usersList.service";

interface UserInfoCardProps {
  userOverride?: IUser;
}

export function UserInfoCard({ userOverride }: UserInfoCardProps) {
  const { user: loggedUser } = useSelector((state: RootState) => state.userInfo);
  const user = userOverride ?? loggedUser;
  const navigate = useNavigate();
  const isOwnProfile = !!loggedUser && user.id === loggedUser.id;

  const roleUpper = String(loggedUser?.role || "").toUpperCase();
  const isSecretaryProfile = String(user.role || "").toUpperCase() === "SECRETARY";
  const isAdminLike = ["ADMIN", "OWNER", "DOCTOR_OWNER"].includes(roleUpper as any);
  const isDoctor = ["DOCTOR", "DOCTOR_OWNER"].includes(roleUpper);
  const canManageSecretaryLinks = isAdminLike && isSecretaryProfile && !isOwnProfile;
  const viewedIsPatient = String(user.role || "").toUpperCase() === "USER";
  const canViewHistory = !isOwnProfile && viewedIsPatient && (isDoctor || isAdminLike);

  const [linkedDoctors, setLinkedDoctors] = useState<{
    doctorId: string;
    name: string;
    email: string;
  }[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<DoctorSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [savingLinks, setSavingLinks] = useState(false);

  useEffect(() => {
    if (!user || !canManageSecretaryLinks) {
      setLinkedDoctors([]);
      return;
    }

    const initial = (user.secretaryDoctors ?? []).map((d) => ({
      doctorId: d.doctorId,
      name: d.user?.name ?? "",
      email: d.user?.email ?? "",
    }));
    setLinkedDoctors(initial);
  }, [user, canManageSecretaryLinks]);

  const handleEditProfile = () => {
    if (!loggedUser) return;
    const roleUpper = String(loggedUser.role || "").toUpperCase();
    const isDoctor = roleUpper === "DOCTOR" || roleUpper === "DOCTOR_OWNER";
    navigate(isDoctor ? ROUTES.EDIT_DOCTOR_PROFILE : ROUTES.EDIT_PROFILE);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "Não informado";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("pt-BR");
    } catch {
      return dateString;
    }
  };

  const formatCPF = (cpf?: string) => {
    if (!cpf) return "Não informado";
    const numbers = cpf.replace(/\D/g, '');
    return numbers
      .slice(0, 11)
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  };

  const formatPhone = (phone?: string) => {
    if (!phone) return "Não informado";
    const numbers = phone.replace(/\D/g, '');
    return numbers
      .slice(0, 11)
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{5})(\d)/, '$1-$2');
  };

  const formatCEP = (cep?: string) => {
    if (!cep) return "Não informado";
    const numbers = cep.replace(/\D/g, '');
    return numbers
      .slice(0, 8)
      .replace(/(\d{5})(\d)/, '$1-$2');
  };

  const handleSearchDoctors = async () => {
    const query = searchTerm.trim();
    if (!query) return;

    try {
      setSearching(true);
      const results = await searchDoctors(query);
      setSearchResults(results);
    } catch (e) {
      console.error(e);
      toast.error("Não foi possível buscar médicos. Tente novamente.");
    } finally {
      setSearching(false);
    }
  };

  const handleAddDoctor = (doc: DoctorSearchResult) => {
    setLinkedDoctors((prev) => {
      if (prev.some((d) => d.doctorId === doc.doctorId)) {
        return prev;
      }
      return [
        ...prev,
        {
          doctorId: doc.doctorId,
          name: doc.name,
          email: doc.email,
        },
      ];
    });
  };

  const handleRemoveLinkedDoctor = (doctorId: string) => {
    setLinkedDoctors((prev) => prev.filter((d) => d.doctorId !== doctorId));
  };

  const handleSaveLinkedDoctors = async () => {
    if (!canManageSecretaryLinks) return;

    try {
      setSavingLinks(true);
      await linkDoctorsToSecretary(
        user.id,
        linkedDoctors.map((d) => d.doctorId),
      );
      toast.success("Vínculos de médicos atualizados com sucesso.");
    } catch (e) {
      console.error(e);
      // toast já tratado no service, mas mantemos aqui por segurança
      toast.error("Não foi possível salvar os vínculos. Tente novamente.");
    } finally {
      setSavingLinks(false);
    }
  };

  return (
    <Card
      sx={{
        padding: 4,
        maxWidth: 800,
        width: "100%",
        margin: "auto",
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight={600} color="#505152ff">
          Perfil do Usuário
        </Typography>
        <Box display="flex" gap={1} alignItems="center">
          {canViewHistory && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<HistoryOutlinedIcon />}
              onClick={() => navigate(`/paciente/${user.id}/historico`)}
              sx={{ borderColor: "#133859", color: "#133859" }}
            >
              Histórico Clínico
            </Button>
          )}
          {isOwnProfile && (
            <IconButton
              size="small"
              color="primary"
              onClick={handleEditProfile}
              aria-label="Editar meu perfil"
            >
              <EditIcon fontSize="small" />
            </IconButton>
          )}
          <Chip 
            label={
              user.role === "admin"
                ? "Administrador"
                : user.role === "DOCTOR"
                  ? "Médico"
                  : "Usuário"
            }
            color={
              user.role === "admin"
                ? "primary"
                : user.role === "DOCTOR"
                  ? "secondary"
                  : "default"
            }
            size="small"
          />
          {user.profile?.isValidated !== undefined && (
            <Chip 
              label={user.profile.isValidated ? "Validado" : "Não Validado"} 
              color={user.profile.isValidated ? "success" : "warning"}
              size="small"
            />
          )}
        </Box>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Informações Básicas */}
      <Box mb={3}>
        <Typography variant="subtitle2" color="text.secondary" mb={1}>
          Informações Básicas
        </Typography>
        <Box display="flex" flexDirection="column" gap={1.5}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Nome
            </Typography>
            <Typography variant="body1" fontWeight={500}>
              {user.name || "Não informado"}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              E-mail
            </Typography>
            <Typography variant="body1" fontWeight={500}>
              {user.email || "Não informado"}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Informações do Perfil */}
      {user.profile && (
        <>
          <Divider sx={{ mb: 3 }} />
          <Box mb={3}>
            <Typography variant="subtitle2" color="text.secondary" mb={1}>
              Dados Pessoais
            </Typography>
            <Box display="flex" flexDirection="column" gap={1.5}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Data de Nascimento
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {formatDate(user.profile.birthDay)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  CPF
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {formatCPF(user.profile.registrationNumber)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Telefone
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {formatPhone(user.profile.phone)}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Divider sx={{ mb: 3 }} />
          <Box mb={3}>
            <Typography variant="subtitle2" color="text.secondary" mb={1}>
              Endereço
            </Typography>
            <Box display="flex" flexDirection="column" gap={1.5}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Endereço Completo
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {user.profile.address || "Não informado"}
                </Typography>
              </Box>
              <Box display="flex" gap={2} flexDirection={{ xs: "column", sm: "row" }}>
                <Box flex={1}>
                  <Typography variant="caption" color="text.secondary">
                    Cidade
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {user.profile.city || "Não informado"}
                  </Typography>
                </Box>
                <Box flex={1}>
                  <Typography variant="caption" color="text.secondary">
                    Estado
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {user.profile.state || "Não informado"}
                  </Typography>
                </Box>
                <Box flex={1}>
                  <Typography variant="caption" color="text.secondary">
                    CEP
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {formatCEP(user.profile.zip_code)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          {user.profile.socialProgram && (
            <>
              <Divider sx={{ mb: 3 }} />
              <Box>
                <Typography variant="subtitle2" color="text.secondary" mb={1}>
                  Programa Social
                </Typography>
                <Box display="flex" flexDirection="column" gap={1.5}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Inscrito no CadÚnico
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      Sim
                    </Typography>
                  </Box>
                  {user.profile.programName && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Nome do Programa
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        {user.profile.programName}
                      </Typography>
                    </Box>
                  )}
                  {user.profile.isValidated !== undefined && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Status de Validação
                      </Typography>
                      <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                        <Chip 
                          label={user.profile.isValidated ? "Validado no CadÚnico" : "Não Validado"} 
                          color={user.profile.isValidated ? "success" : "warning"}
                          size="small"
                        />
                        {user.profile.level && (
                          <Chip 
                            label={`Nível ${user.profile.level}`} 
                            color="info"
                            size="small"
                          />
                        )}
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            </>
          )}
          
          {user.profile.socialProgram === false && (
            <>
              <Divider sx={{ mb: 3 }} />
              <Box>
                <Typography variant="subtitle2" color="text.secondary" mb={1}>
                  Programa Social
                </Typography>
                <Box display="flex" flexDirection="column" gap={1.5}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Inscrito no CadÚnico
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      Não
                    </Typography>
                  </Box>
                  {user.profile.level && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Nível
                      </Typography>
                      <Box mt={0.5}>
                        <Chip 
                          label={`Nível ${user.profile.level}`} 
                          color="info"
                          size="small"
                        />
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            </>
          )}
        </>
      )}

      {!user.profile && user.role === "user" && (
        <>
          <Divider sx={{ mb: 3 }} />
          <Box textAlign="center" py={2}>
            <Typography variant="body2" color="text.secondary">
              Você ainda não completou seu perfil
            </Typography>
          </Box>
        </>
      )}

      {/* Informações do médico / clínicas */}
      {(["DOCTOR", "DOCTOR_OWNER"].includes(String(user.role || "").toUpperCase()) && user.doctor) && (
        <>
          <Divider sx={{ mb: 3 }} />
          <Box mb={3}>
            <Typography variant="subtitle2" color="text.secondary" mb={1}>
              Informações do médico
            </Typography>
            <Box display="flex" flexDirection="column" gap={2}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Código do médico
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {user.doctor.doctorId || "Não informado"}
                </Typography>
              </Box>

              {user.doctor.locals && user.doctor.locals.length > 0 ? (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Locais de atendimento
                  </Typography>
                  <Box display="flex" flexDirection="column" gap={1.5} mt={0.5}>
                    {user.doctor.locals.map((local) => (
                      <Box
                        key={local.id}
                        sx={{
                          borderRadius: 1,
                          border: "1px solid #e0e0e0",
                          padding: 1.5,
                        }}
                      >
                        <Typography variant="body2" fontWeight={600}>
                          {local.localName || "Clínica / Consultório"}
                        </Typography>
                        {(local.address || local.city || local.state) && (
                          <Typography variant="body2" color="text.secondary">
                            {local.address || ""}
                            {local.city ? ` - ${local.city}` : ""}
                            {local.state ? ` / ${local.state}` : ""}
                          </Typography>
                        )}
                        {(local.zip_code || local.phone) && (
                          <Typography variant="caption" color="text.secondary">
                            {local.zip_code ? `CEP: ${formatCEP(local.zip_code)}` : ""}
                            {local.zip_code && local.phone ? " • " : ""}
                            {local.phone ? `Tel: ${formatPhone(local.phone)}` : ""}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Box>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Nenhum local de atendimento cadastrado ainda.
                </Typography>
              )}
            </Box>
          </Box>
        </>
      )}

      {/* Gestão de vínculos SECRETARY ↔ DOCTOR para ADMIN/OWNER/DOCTOR_OWNER */}
      {canManageSecretaryLinks && (
        <>
          <Divider sx={{ mb: 3 }} />
          <Box mb={3}>
            <Typography variant="subtitle2" color="text.secondary" mb={1}>
              Médicos vinculados à secretária
            </Typography>

            <Box display="flex" flexDirection="column" gap={1.5}>
              {linkedDoctors.length > 0 ? (
                linkedDoctors.map((d) => (
                  <Box
                    key={d.doctorId}
                    sx={{
                      borderRadius: 1,
                      border: "1px solid #e0e0e0",
                      padding: 1.5,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {d.name || d.doctorId}
                      </Typography>
                      {d.email && (
                        <Typography variant="body2" color="text.secondary">
                          {d.email}
                        </Typography>
                      )}
                    </Box>
                    <Button
                      size="small"
                      color="error"
                      onClick={() => handleRemoveLinkedDoctor(d.doctorId)}
                    >
                      Remover
                    </Button>
                  </Box>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Nenhum médico vinculado ainda.
                </Typography>
              )}
            </Box>

            <Box mt={2}>
              <Typography variant="subtitle2" color="text.secondary" mb={1}>
                Buscar médicos para vincular
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                <TextField
                  label="Nome, CRM ou CPF do médico"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  fullWidth
                />
                <Button
                  variant="outlined"
                  onClick={handleSearchDoctors}
                  disabled={searching || !searchTerm.trim()}
                >
                  {searching ? "Buscando..." : "Buscar"}
                </Button>
              </Stack>

              {searchResults.length > 0 && (
                <Box mt={1.5} display="flex" flexDirection="column" gap={1}>
                  {searchResults.map((doc) => {
                    const alreadyLinked = linkedDoctors.some(
                      (d) => d.doctorId === doc.doctorId,
                    );
                    return (
                      <Box
                        key={doc.doctorId}
                        sx={{
                          borderRadius: 1,
                          border: "1px solid #f0f0f0",
                          padding: 1,
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            {doc.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {doc.email}
                          </Typography>
                        </Box>
                        <Button
                          size="small"
                          variant="text"
                          disabled={alreadyLinked}
                          onClick={() => handleAddDoctor(doc)}
                        >
                          {alreadyLinked ? "Já vinculado" : "Vincular"}
                        </Button>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>

            <Box mt={2} display="flex" justifyContent="flex-end">
              <Button
                variant="contained"
                onClick={handleSaveLinkedDoctors}
                disabled={savingLinks}
              >
                {savingLinks ? "Salvando..." : "Salvar vínculos"}
              </Button>
            </Box>
          </Box>
        </>
      )}
    </Card>
  );
}
